import type { LocationQuery } from 'vue-router'
import { LEADERBOARD_SEARCH_MIN_LENGTH } from '../stores/leaderboard'

const PICKEM_VIEW_QUERY = 'view'
const PICKEM_VIEW_LEADERBOARD = 'leaderboard'
const PICKEM_LEADERBOARD_PAGE_QUERY = 'page'
const PICKEM_LEADERBOARD_SEARCH_QUERY = 'search'

const parseQueryValue = (
  query: LocationQuery,
  key: string
): string | undefined => {
  const raw = query[key]
  const value = Array.isArray(raw) ? raw[0] : raw

  return value === undefined || value === null ? undefined : String(value)
}

export const isPickemLeaderboardOpen = (query: LocationQuery): boolean =>
  parseQueryValue(query, PICKEM_VIEW_QUERY) === PICKEM_VIEW_LEADERBOARD

export const parsePickemLeaderboardPage = (query: LocationQuery): number => {
  const raw = query[PICKEM_LEADERBOARD_PAGE_QUERY]
  const value = Array.isArray(raw) ? raw[0] : raw
  const parsed = Number(value)

  return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1
}

export const parsePickemLeaderboardSearch = (query: LocationQuery): string => {
  const raw = query[PICKEM_LEADERBOARD_SEARCH_QUERY]
  const value = Array.isArray(raw) ? raw[0] : raw

  return typeof value === 'string' ? value : ''
}

const buildLeaderboardQuery = (
  currentQuery: LocationQuery,
  state: { open: boolean; page?: number; search?: string }
): LocationQuery => {
  const query: LocationQuery = { ...currentQuery }

  if (!state.open) {
    if (parseQueryValue(query, PICKEM_VIEW_QUERY) === PICKEM_VIEW_LEADERBOARD) {
      delete query[PICKEM_VIEW_QUERY]
    }
    delete query[PICKEM_LEADERBOARD_PAGE_QUERY]
    delete query[PICKEM_LEADERBOARD_SEARCH_QUERY]
    return query
  }

  query[PICKEM_VIEW_QUERY] = PICKEM_VIEW_LEADERBOARD

  if (state.page && state.page > 1) {
    query[PICKEM_LEADERBOARD_PAGE_QUERY] = String(state.page)
  } else {
    delete query[PICKEM_LEADERBOARD_PAGE_QUERY]
  }

  const trimmedSearch = state.search?.trim() ?? ''
  if (trimmedSearch) {
    query[PICKEM_LEADERBOARD_SEARCH_QUERY] = trimmedSearch
  } else {
    delete query[PICKEM_LEADERBOARD_SEARCH_QUERY]
  }

  return query
}

const isSameLeaderboardQuery = (
  currentQuery: LocationQuery,
  nextQuery: LocationQuery
): boolean =>
  parseQueryValue(currentQuery, PICKEM_VIEW_QUERY) ===
    parseQueryValue(nextQuery, PICKEM_VIEW_QUERY) &&
  parseQueryValue(currentQuery, PICKEM_LEADERBOARD_PAGE_QUERY) ===
    parseQueryValue(nextQuery, PICKEM_LEADERBOARD_PAGE_QUERY) &&
  parseQueryValue(currentQuery, PICKEM_LEADERBOARD_SEARCH_QUERY) ===
    parseQueryValue(nextQuery, PICKEM_LEADERBOARD_SEARCH_QUERY)

export const usePickemLeaderboardRoute = () => {
  const route = useRoute()
  const router = useRouter()

  const isOpen = computed(() => isPickemLeaderboardOpen(route.query))
  const page = computed(() => parsePickemLeaderboardPage(route.query))
  const search = computed(() => parsePickemLeaderboardSearch(route.query))

  const open = (options?: { page?: number; search?: string }) => {
    const nextQuery = buildLeaderboardQuery(route.query, {
      open: true,
      page: options?.page ?? 1,
      search: options?.search ?? ''
    })

    if (isSameLeaderboardQuery(route.query, nextQuery)) return

    return router.push({ query: nextQuery })
  }

  const close = () => {
    const nextQuery = buildLeaderboardQuery(route.query, { open: false })

    if (isSameLeaderboardQuery(route.query, nextQuery)) return

    return router.replace({ query: nextQuery })
  }

  const syncState = (state: { page: number; search: string }) => {
    if (!isOpen.value) return

    const nextQuery = buildLeaderboardQuery(route.query, {
      open: true,
      page: state.page,
      search: state.search
    })

    if (isSameLeaderboardQuery(route.query, nextQuery)) return

    return router.replace({ query: nextQuery })
  }

  const matchesStoreState = (storeState: {
    page: number
    searchInput: string
    appliedSearch: string
  }) => {
    const routeSearch = search.value
    const routeAppliedSearch =
      routeSearch.trim().length >= LEADERBOARD_SEARCH_MIN_LENGTH
        ? routeSearch.trim()
        : ''

    return (
      storeState.page === page.value &&
      storeState.searchInput === routeSearch &&
      storeState.appliedSearch === routeAppliedSearch
    )
  }

  return {
    isOpen,
    page,
    search,
    open,
    close,
    syncState,
    matchesStoreState
  }
}
